// Keyboard bindings for the studio editor shell (Edit mode). MapEditorOverlay
// binds its own shortcuts but only mounts with chrome='full' - unused inside
// this app (Studio's own Edit mode always renders chrome='viewport'), but a
// real published @sstk/build-kit export that sstk-dwindling still renders
// with chrome='full' (its own in-game level-editor console), so it stays.
// This hook gives the chrome='viewport' shell the same store-backed editing
// power: undo/redo, delete, duplicate, copy/paste, select all, nudge, rotate,
// focus, manual save, and the gizmo tools the viewport toolbar advertises.
import { useEffect } from 'react';

import { getObjectDefinition, useEngineRuntimeStore, useMapEditorStore, type MapFloorLevel } from '../../engine';

/** Deleting more objects than this asks for confirmation first. */
export const DELETE_CONFIRM_THRESHOLD = 3;

function nudgeStep(): number {
  const state = useMapEditorStore.getState();
  if (state.snapMode === 'off') return 0.1;
  if (state.selectedObjectIds.length === 0) return 0.1;
  const firstId = state.selectedObjectIds[0];
  const instance = state.objectInstances.find((inst) => inst.id === firstId);
  const definition = instance ? getObjectDefinition(instance.assetId) : null;
  return definition?.editor.snap ?? 0.1;
}

export function inEditableTarget(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (el as HTMLElement).isContentEditable;
}

/**
 * Binds the editor shortcuts while mounted. Deletes route through
 * onDeleteRequest so the host can confirm large selections before calling
 * the store's deleteSelected.
 */
export function useEditorShortcuts(onDeleteRequest: () => void, onToggleShortcuts?: () => void) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const store = useMapEditorStore.getState();
      if (!store.enabled) return;
      // Typing in a field keeps native behaviour (text undo, caret movement).
      if (inEditableTarget()) return;
      const mod = event.ctrlKey || event.metaKey;
      // While the pointer is locked the camera owns the letter keys (WASD
      // fly), so bare-letter tools only apply outside free-fly.
      const flying = document.pointerLockElement !== null;

      if (mod && event.code === 'KeyZ' && !event.shiftKey && !event.repeat) {
        event.preventDefault();
        store.undo();
        return;
      }
      if (mod && ((event.code === 'KeyZ' && event.shiftKey) || event.code === 'KeyY') && !event.repeat) {
        event.preventDefault();
        store.redo();
        return;
      }
      if (mod && event.code === 'KeyS' && !event.repeat) {
        event.preventDefault();
        void store.saveToDisk();
        return;
      }
      if (mod && event.code === 'KeyA' && !event.repeat) {
        event.preventDefault();
        store.selectAll(useEngineRuntimeStore.getState().currentFloor as MapFloorLevel);
        return;
      }
      if (mod && event.code === 'KeyD' && !event.repeat) {
        event.preventDefault();
        if (store.selectedObjectIds.length === 1) {
          store.duplicateObject(store.selectedObjectIds[0]!);
        }
        return;
      }
      if (mod && event.code === 'KeyC' && !event.repeat) {
        event.preventDefault();
        store.copySelected();
        return;
      }
      if (mod && event.code === 'KeyV' && !event.repeat) {
        event.preventDefault();
        const hit = store.lastRaycastHit;
        if (hit) {
          store.pasteAtPosition(hit[0], hit[1], useEngineRuntimeStore.getState().currentFloor as MapFloorLevel);
        }
        return;
      }
      if (mod) return;

      if ((event.code === 'Delete' || event.code === 'Backspace') && !event.repeat) {
        event.preventDefault();
        onDeleteRequest();
        return;
      }
      // While flying, Escape's job is exiting pointer lock; leave the
      // selection alone.
      if (event.code === 'Escape' && !event.repeat && !flying) {
        if (store.pendingAssetId) {
          store.setPendingAssetId(null);
        } else {
          store.clearSelection();
        }
        return;
      }

      // Arrow-key nudging (shift = 10x)
      const arrowMap: Record<string, [number, number]> = {
        ArrowLeft: [-1, 0],
        ArrowRight: [1, 0],
        ArrowUp: [0, -1],
        ArrowDown: [0, 1],
      };
      const arrow = arrowMap[event.code];
      if (arrow && !flying) {
        if (store.selectedObjectIds.length === 0) return;
        event.preventDefault();
        const step = nudgeStep() * (event.shiftKey ? 10 : 1);
        store.nudgeSelected([arrow[0] * step, 0, arrow[1] * step]);
        return;
      }

      if (flying || event.repeat) return;

      // The viewport toolbar's advertised tools: V select, G move, R rotate,
      // S scale.
      if (event.code === 'KeyV') {
        store.setGizmoMode('off');
        return;
      }
      if (event.code === 'KeyG') {
        store.setGizmoMode('translate');
        return;
      }
      if (event.code === 'KeyR') {
        store.setGizmoMode('rotate');
        return;
      }
      if (event.code === 'KeyS') {
        store.setGizmoMode('scale');
        return;
      }

      // Q/E rotate the placement ghost during placement, otherwise the
      // selected object.
      if (event.code === 'KeyQ' || event.code === 'KeyE') {
        const delta = event.code === 'KeyQ' ? -Math.PI / 4 : Math.PI / 4;
        event.preventDefault();
        if (store.pendingAssetId) {
          store.rotatePlacementBy(delta);
        } else if (store.selectedObjectIds.length === 1) {
          store.rotateObject(store.selectedObjectIds[0]!, delta);
        }
        return;
      }
      if (event.key === '?' && !event.repeat) {
        event.preventDefault();
        onToggleShortcuts?.();
        return;
      }

      if (event.code === 'KeyF' && store.selectedObjectIds.length === 1) {
        event.preventDefault();
        store.focusOnInstance(store.selectedObjectIds[0]!, {
          currentFloor: useEngineRuntimeStore.getState().currentFloor as MapFloorLevel,
        });
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onDeleteRequest, onToggleShortcuts]);
}
